import { DestroyRef, inject, signal, WritableSignal } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { catchError, debounceTime, distinctUntilChanged, EMPTY, finalize, map, Observable, of, switchMap } from 'rxjs'
import { CertCertificateHolderApiResponse, CertificateHolderApiResponse } from '@features/certificate-holder/models/certificate-holder.model'
import { CertificateHolderService } from '@features/certificate-holder/services/certificate-holder.service'
import { GlobalErrorService } from '@shared/services/global-error.service'

export enum CertificateHolderRowType {
  HOLDER = 'holder',
  ADDRESS = 'address'
}

/**
 * A flattened table row that represents either a certificate holder or one of its recipient
 * addresses. Both the certificate-holder and the ACR components render exactly the same shape,
 * so the type is shared here instead of being redeclared per component.
 */
export interface CertificateHolderRow {
  type: CertificateHolderRowType
  uuid: string | null
  holderIndex: number
  addressIndex: number
  companyName: string
  country: string
  email: string
  freetext: string
}

/**
 * Flattens the already-added holders into `holder` + `address` rows for the display table.
 */
export function buildGroupedRows(holders: CertCertificateHolderApiResponse[]): CertificateHolderRow[] {
  const rows: CertificateHolderRow[] = []
  for (let hi = 0; hi < holders.length; hi++) {
    const holder = holders[hi]!
    rows.push({
      type: CertificateHolderRowType.HOLDER,
      uuid: holder.uuid ?? null,
      holderIndex: hi,
      addressIndex: -1,
      companyName: holder.companyName,
      country: holder.country,
      email: '',
      freetext: holder.freetext
    })
    const emails = holder.certificateHolderRecipientAddresses ?? []
    for (let ai = 0; ai < emails.length; ai++) {
      rows.push({
        type: CertificateHolderRowType.ADDRESS,
        uuid: holder.uuid ?? null,
        holderIndex: hi,
        addressIndex: ai,
        companyName: '',
        country: '',
        email: emails[ai]!.email,
        freetext: holder.freetext
      })
    }
  }
  return rows
}

/**
 * Flattens search results into `holder` + `address` rows, hiding emails that are already added
 * to the same holder in `existingHolders` (and holders whose emails are all added). The same email
 * stays available under a different holder.
 */
export function buildSearchResultRows(
  searchResults: CertificateHolderApiResponse[],
  existingHolders: CertCertificateHolderApiResponse[]
): CertificateHolderRow[] {
  const rows: CertificateHolderRow[] = []
  for (let hi = 0; hi < searchResults.length; hi++) {
    const holder = searchResults[hi]!
    const existingHolder = existingHolders.find(h => h.uuid === holder.uuid)
    const addedEmails = new Set((existingHolder?.certificateHolderRecipientAddresses ?? []).map(a => a.email))
    const emails = (holder.recipientAddresses ?? []).filter(a => !addedEmails.has(a.email))
    if (existingHolder !== undefined && emails.length === 0) continue
    rows.push({
      type: CertificateHolderRowType.HOLDER,
      uuid: holder.uuid ?? null,
      holderIndex: hi,
      addressIndex: -1,
      companyName: holder.companyName,
      country: holder.country,
      email: '',
      freetext: holder.freetext
    })
    for (const emailEntry of emails) {
      const originalIndex = (holder.recipientAddresses ?? []).indexOf(emailEntry)
      rows.push({
        type: CertificateHolderRowType.ADDRESS,
        uuid: holder.uuid ?? null,
        holderIndex: hi,
        addressIndex: originalIndex,
        companyName: '',
        country: '',
        email: emailEntry.email,
        freetext: holder.freetext
      })
    }
  }
  return rows
}

/**
 * Shared search state and behaviour for the certificate-holder and ACR components.
 *
 * `init()` wires the debounced search on the form's `value` control and must be called from a
 * lifecycle hook (e.g. `ngOnInit`) so the subscription is torn down via the captured `DestroyRef`.
 */
export interface CertificateHolderSearch {
  searchForm: FormGroup<{ value: FormControl<string | null> }>
  searchQuery: WritableSignal<string>
  searchResults: WritableSignal<CertificateHolderApiResponse[]>
  isSearching: WritableSignal<boolean>
  init: () => void
}

/**
 * Creates the shared certificate-holder search controller. Must be called within an injection
 * context (e.g. a component field initializer or constructor) because it uses `inject`.
 */
export function createCertificateHolderSearch(): CertificateHolderSearch {
  const certificateHolderService = inject(CertificateHolderService)
  const globalErrorService = inject(GlobalErrorService)
  const destroyRef = inject(DestroyRef)

  const searchForm = new FormGroup({
    value: new FormControl('')
  })
  const searchQuery = signal('')
  const searchResults = signal<CertificateHolderApiResponse[]>([])
  const isSearching = signal(false)

  const performSearch = (query: string): Observable<CertificateHolderApiResponse[]> => {
    isSearching.set(true)
    return certificateHolderService.search(query).pipe(
      catchError(() => {
        globalErrorService.reportError({
          subsystemName: 'Certificate Holder Search',
          description: 'The certificate holders could not be searched.'
        })
        return of([])
      }),
      finalize(() => isSearching.set(false))
    )
  }

  const init = (): void => {
    searchForm.controls.value.valueChanges.pipe(
      takeUntilDestroyed(destroyRef),
      debounceTime(300),
      map(val => (val ?? '').trim()),
      distinctUntilChanged(),
      switchMap(query => {
        searchQuery.set(query)
        if (query.length < 3) {
          searchResults.set([])
          return EMPTY
        }
        return performSearch(query)
      })
    ).subscribe(results => searchResults.set(results))
  }

  return { searchForm, searchQuery, searchResults, isSearching, init }
}
